import { Card, DialogProps, Typography } from '@mui/material';
import { QueryKey, WorkflowExecutionSummary, WorkflowNode } from 'api/model.ts';
import InfoDialog from './InfoDialog';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { getWorkflowNodesCompletedExecutionsByPrompt } from 'api/workflows/requests';
import { useEffect, useMemo } from 'react';
import Loader from 'components/Loader';
import { toast } from 'react-toastify';
import { ToastNodeItem } from 'views/toasts/WorkflowCard';
import ExecutionSelect from 'components/form/ExecutionSelect';

export type ExecutedNodeData = {
  node: WorkflowNode;
  workflowExecutionId: string;
};

type NodeGroup = {
  node: WorkflowNode;
  executions: { node: WorkflowNode; execution: WorkflowExecutionSummary }[];
};

type Props = DialogProps & {
  promptId: string;
  workflowId: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ExecutedNodeData) => void;
};

const PromptInputExecutionSelectDialog = ({ promptId, workflowId, open, onClose, onSubmit }: Props) => {
  const { projectId = '' } = useParams();
  const { data: promptUsages } = useQuery({
    queryKey: [QueryKey.WorkflowExecutions, { workflowId, promptId }],
    queryFn: () => getWorkflowNodesCompletedExecutionsByPrompt(projectId, workflowId, promptId),
    enabled: open,
  });

  const groupedNodes = useMemo(() => {
    if (!promptUsages) {
      return undefined;
    }
    const uniqueNodesMap = new Map<string, NodeGroup>();
    Object.entries(promptUsages.completed_nodes).forEach(([executionId, nodes]) => {
      const execution = promptUsages.executions.find((e) => e.id === executionId);
      if (execution) {
        nodes.forEach((node) => {
          const key = `${node.name}::${node.type}`;
          const existing = uniqueNodesMap.get(key);
          if (existing) {
            existing.executions.push({ node, execution });
          } else {
            uniqueNodesMap.set(key, { node, executions: [{ node, execution }] });
          }
        });
      }
    });
    return [...uniqueNodesMap.values()];
  }, [promptUsages]);

  const handleSelectNode = (node: WorkflowNode, execution: WorkflowExecutionSummary) => {
    onSubmit({ node, workflowExecutionId: execution.id });
    onClose();
  };

  useEffect(() => {
    if (open && promptUsages?.executions.length === 0) {
      toast.warning('No completed nodes using this prompt found in the workflow.', { toastId: 'no-completed-nodes' });
      onClose();
    }
  }, [promptUsages, open, onClose]);

  if (!promptUsages) {
    return (
      <InfoDialog
        open={open}
        buttonsSettings={{ confirmText: 'Close' }}
        onCancel={onClose}
        onConfirm={onClose}
        contentSx={{ overflowY: 'visible' }}
      >
        <Loader />
      </InfoDialog>
    );
  }

  return (
    <InfoDialog
      open={open}
      buttonsSettings={{ confirmText: 'Close' }}
      onCancel={onClose}
      onConfirm={onClose}
      contentSx={{ overflowY: 'visible' }}
    >
      <Typography variant="subtitle1" className="mb-4">
        Select a node execution to load input from:
      </Typography>
      {groupedNodes?.map((group) => {
        const latestExecution = group.executions[0].execution;
        return (
          <Card
            key={`${group.node.name}-${group.node.type}`}
            variant="outlined"
            className="mb-2 p-4 flex flex-col gap-4 min-w-[400px] cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            onClick={() => handleSelectNode(group.node, latestExecution)}
          >
            <ToastNodeItem node={group.node} />
            <div onClick={(event) => event.stopPropagation()}>
              <ExecutionSelect
                workflowId={workflowId}
                label="Select execution"
                options={group.executions.map(({ execution }) => execution)}
                latest={latestExecution}
                value={latestExecution.id}
                onChange={(executionId) => {
                  const execution = group.executions.find(({ execution }) => execution.id === executionId)?.execution;
                  if (execution) {
                    handleSelectNode(group.node, execution);
                  }
                }}
              />
            </div>
          </Card>
        );
      })}
    </InfoDialog>
  );
};

export default PromptInputExecutionSelectDialog;
